import {
  Container,
  SceneObject,
  VideoResourcePool,
  VideoResource,
  IConcreteResourceCache,
  VideoDataImpl,
  VideoData,
} from '@cgs/wge';
import { SceneCommon, SCENE_COMMON_TOKEN } from '@cgs/common';
import { IconsSceneObjectComponent } from './icons_scene_object_component';
import {
  T_IconsSceneObjectComponent,
  T_ISlotGame,
  T_LinesSceneObjectComponent,
} from '../../type_definitions';
import { LinesSceneObjectComponent } from './lines_scene_object_component';

export const TIME_SCALE_COMPONENT_TOKEN = Symbol('TIME_SCALE_COMPONENT_TOKEN');

interface ISlotGame {
  timeScale: number;
  findById(id: string): SceneObject | null;
}

export class TimeScaleComponent {
  private readonly slotGame: ISlotGame;
  private readonly iconSceneObject: SceneObject;
  private readonly animIconSceneObject: SceneObject | null;
  private readonly linesSceneObject: SceneObject;
  private readonly resourceCache: IConcreteResourceCache;

  private _skippedTimeScale: number = 1;

  private previousTimeScale: number;
  private _isPaused: boolean = false;

  // Videos paused by the low-rate branch of setAllVideosPlaybackRate — only
  // these may be auto-resumed when the rate goes back up. Force-playing every
  // pooled video would start them invisibly and pollute currentTime, breaking
  // the first visible playback.
  private readonly videosPausedByLowRate = new Set<HTMLVideoElement>();

  constructor(protected readonly container: Container) {
    this.slotGame = container.resolve<ISlotGame>(T_ISlotGame);
    const iconsSceneObjectComponent = container.resolve<IconsSceneObjectComponent>(
      T_IconsSceneObjectComponent
    );
    this.iconSceneObject = iconsSceneObjectComponent.iconsRender.parent!;
    this.animIconSceneObject = iconsSceneObjectComponent.animIconsRender.parent || null;
    this.linesSceneObject = container.resolve<LinesSceneObjectComponent>(
      T_LinesSceneObjectComponent
    ).linesSceneObject.parent!;
    this.resourceCache = container.resolve<SceneCommon>(SCENE_COMMON_TOKEN)
      .resourceCache as IConcreteResourceCache;

    this.previousTimeScale = this.slotGame.timeScale;
  }

  public get isPaused(): boolean {
    return this._isPaused;
  }

  public set skippedTimeScale(scale: number) {
    this._skippedTimeScale = scale;
  }

  public set timeScale(scale: number) {
    if (this.slotGame.timeScale === scale) {
      return;
    }

    this.slotGame.timeScale = scale;
    this.iconSceneObject.timeScale = scale;
    if (this.animIconSceneObject) {
      this.animIconSceneObject.timeScale = scale;
    }
    this.linesSceneObject.timeScale = scale;

    const popup = this.slotGame.findById('popup')?.parent;
    if (popup) {
      popup.timeScale = scale;
    }

    const machineScale = this.slotGame.findById('machineScale')?.parent;
    if (machineScale) {
      machineScale.timeScale = scale;
    }

    this.setAllVideosPlaybackRate(scale);
  }

  public get timeScale(): number {
    return this.slotGame.timeScale;
  }

  public get skippedTimeScale(): number {
    return this._skippedTimeScale;
  }

  public stopGame(): void {
    this.previousTimeScale = this.timeScale;
    this.timeScale = 0;
    this.pauseAllVideos();
    this._isPaused = true;
  }

  public resumeGame(): void {
    this.timeScale = this.previousTimeScale;
    this.resumeAllVideos();
    this._isPaused = false;
  }

  private pauseAllVideos(): void {
    this.eachVideo((videoData: VideoData, key) => {
      try {
        videoData.pause();
      } catch (error) {
        console.error(`[TimeScaleComponent]: Pause error for VideoResource: ${key}`, error);
      }
    });
  }

  private resumeAllVideos(): void {
    this.eachVideo((videoData: VideoData, key) => {
      try {
        videoData.resume();
      } catch (error) {
        console.error(`[TimeScaleComponent]: Resume error for VideoResource: ${key}`, error);
      }
    });
  }

  private setAllVideosPlaybackRate(scale: number): void {
    const MIN_PLAYBACK_RATE = 0.0625;

    this.eachVideo((videoData: VideoData) => {
      if (videoData instanceof VideoDataImpl) {
        const video = videoData.video;

        if (scale < MIN_PLAYBACK_RATE) {
          video.playbackRate = MIN_PLAYBACK_RATE;
          if (!video.paused) {
            this.videosPausedByLowRate.add(video);
            video.pause();
          }
        } else {
          video.playbackRate = scale;
          if (this.videosPausedByLowRate.delete(video)) {
            video.play();
          }
        }
      }
    });
  }

  protected eachVideo(callback: (videoData: VideoData, key: string) => void): void {
    const videoPool = this.resourceCache.getPool('video');

    if (videoPool instanceof VideoResourcePool) {
      const resources = videoPool.resources;

      resources.forEach((resource: VideoResource, key: string) => {
        const videoData = resource.data;

        if (videoData) {
          callback(videoData, key);
        }
      });
    }
  }
}
